import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import toast from "react-hot-toast";
import {
  deleteDocById,
  deleteObjectByUrl,
} from "../../Firebase/Firebase.utils";
import { usePaginatedDocs } from "../hooks/usePaginatedDocs";
import { isPublished } from "../utils/cmsSchema";
import PageHeader from "./PageHeader";
import SearchFilterBar from "./SearchFilterBar";
import ContentCard from "./ContentCard";
import EmptyState from "./EmptyState";
import ConfirmDialog from "./ConfirmDialog";
import Pagination from "./Pagination";
import ViewModeToggle, { useViewMode } from "./ViewModeToggle";
import { revalidateFor } from "../utils/revalidatePublic";

const PAGE_SIZE = 12;

export default function ContentListGrid({
  schema,
  title,
  subtitle,
  newRoute,
  metaFor,
}) {
  const navigate = useNavigate();
  const [activeSearch, setActiveSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const {
    docs,
    page,
    totalCount,
    totalPages,
    pageSize,
    isLoading,
    isPaging,
    goToPage,
    refresh,
    removeDoc,
  } = usePaginatedDocs({
    schema,
    pageSize: PAGE_SIZE,
    search: activeSearch,
    statusFilter,
  });

  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // List is the default because it avoids fetching thumbnail bytes for
  // every row. Ops can flip to card view when they specifically want
  // to see images (e.g. reviewing new blog covers). Preference is
  // persisted per collection.
  const [viewMode, setViewMode] = useViewMode(schema.key, "list");
  const canSwitchModes = Boolean(schema.heroField);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      const heroUrl = pendingDelete[schema.heroField];
      const pdfUrl = pendingDelete.pdf;
      await deleteDocById(schema.key, pendingDelete.id);
      revalidateFor(schema.key);
      removeDoc(pendingDelete.id);
      if (heroUrl) {
        try {
          await deleteObjectByUrl(heroUrl);
        } catch {}
      }
      if (pdfUrl) {
        try {
          await deleteObjectByUrl(pdfUrl);
        } catch {}
      }
      toast.success(`${schema.singular} deleted.`);
      setPendingDelete(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete.");
    } finally {
      setIsDeleting(false);
    }
  };

  const isEmpty = !isLoading && docs.length === 0;
  const isFilteredEmpty =
    isEmpty && (activeSearch.trim() !== "" || statusFilter !== "all");

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <button
            type="button"
            onClick={() => navigate(newRoute)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-leechiuOrange text-white text-sm font-medium hover:opacity-90 shadow-sm"
          >
            <Plus className="w-4 h-4" />
            New {schema.singular}
          </button>
        }
      />

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <SearchFilterBar
            activeSearch={activeSearch}
            onSearch={setActiveSearch}
            status={statusFilter}
            setStatus={setStatusFilter}
            placeholder={`Search ${schema.label.toLowerCase()} by title…`}
          />
        </div>
        {canSwitchModes ? (
          <div className="pt-1 shrink-0">
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
          </div>
        ) : null}
      </div>

      {isLoading ? (
        viewMode === "card" && schema.heroField ? (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl ring-1 ring-gray-100 overflow-hidden"
              >
                <div className="aspect-[16/9] bg-gray-100 animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="h-3 w-1/3 bg-gray-100 animate-pulse rounded" />
                  <div className="h-4 w-3/4 bg-gray-100 animate-pulse rounded" />
                  <div className="h-3 w-full bg-gray-100 animate-pulse rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-xl ring-1 ring-gray-100 px-4 py-3 flex items-center gap-4"
              >
                <div className="w-16 h-4 bg-gray-100 animate-pulse rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-1/2 bg-gray-100 animate-pulse rounded" />
                  <div className="h-4 w-2/3 bg-gray-100 animate-pulse rounded" />
                </div>
                <div className="w-20 h-6 bg-gray-100 animate-pulse rounded" />
              </div>
            ))}
          </div>
        )
      ) : isEmpty ? (
        <EmptyState
          title={
            isFilteredEmpty
              ? "No matches"
              : `No ${schema.label.toLowerCase()} yet`
          }
          message={
            isFilteredEmpty
              ? activeSearch
                ? `Nothing matches "${activeSearch}". Try a different keyword or clear the search.`
                : "Try clearing the filter."
              : `Create your first ${schema.singular.toLowerCase()} to get started.`
          }
          actionLabel={
            !isFilteredEmpty ? `New ${schema.singular}` : undefined
          }
          onAction={
            !isFilteredEmpty ? () => navigate(newRoute) : undefined
          }
        />
      ) : (
        <>
          <div
            className={`${
              // Card view (only available when schema has a heroField):
              // 3-col image-first grid. List view: single-column
              // horizontal-row stack. Collections without a heroField
              // are always list (no card option offered).
              viewMode === "card" && schema.heroField
                ? "grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                : "flex flex-col gap-2"
            } transition-opacity ${isPaging ? "opacity-60" : "opacity-100"}`}
          >
            {docs.map((doc) => (
              <ContentCard
                key={doc.id}
                doc={doc}
                heroField={schema.heroField}
                heroAspect={schema.heroAspect}
                heroObjectPosition={schema.heroObjectPosition}
                dateField={
                  // Explicit `dateField: null` (e.g. leaders — sortOrder
                  // isn't a date) wins. Otherwise fall back to the
                  // orderBy field, which for blogs/news is the same as
                  // the post date and for careers is postedAt.
                  "dateField" in schema
                    ? schema.dateField
                    : schema.orderByField || "date"
                }
                editTo={`${schema.route}/${doc.id}`}
                published={isPublished(doc, schema)}
                meta={metaFor ? metaFor(doc) : undefined}
                onDelete={(d) => setPendingDelete(d)}
                viewMode={viewMode}
              />
            ))}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            isPaging={isPaging}
            onChange={goToPage}
          />

          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={refresh}
              className="text-xs text-gray-400 hover:text-leechiuBlue underline-offset-2 hover:underline"
            >
              Refresh
            </button>
          </div>
        </>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title={`Delete this ${schema.singular.toLowerCase()}?`}
        message={
          pendingDelete
            ? `"${pendingDelete.title || "Untitled"}" will be permanently removed.`
            : ""
        }
        confirmLabel={isDeleting ? "Deleting…" : "Delete"}
        destructive
        onConfirm={handleDelete}
        onCancel={() => !isDeleting && setPendingDelete(null)}
      />
    </div>
  );
}
